/**
 * Sentry init + thin wrappers used everywhere we want to capture an error
 * with consistent context. Init is idempotent and a no-op when
 * `VITE_SENTRY_DSN` isn't set, so dev runs without a DSN don't blow up.
 */
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { BRAND } from "@/brand.config";

let initialized = false;

export function initSentry(): void {
  if (initialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    if (import.meta.env.MODE !== "development") {
      // Production missing a DSN is worth flagging in console; in dev it's
      // expected and would just spam.
      console.warn("[Sentry] VITE_SENTRY_DSN not set; error reporting disabled.");
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,

    integrations: [
      // React Router v6 instrumentation: route-change breadcrumbs +
      // auto-tagged transactions per route.
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Session Replay is added below via addIntegration after the
      // browser is idle — keeps the public homepage's critical-path
      // bundle smaller. The integration is only attached on admin
      // routes where it's actually useful for debugging.
    ],

    // Performance traces
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.2 : 1.0,

    // Session Replay sample rates
    replaysSessionSampleRate:
      import.meta.env.MODE === "production" ? 0.05 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Don't auto-attach IP, cookies, or browser fingerprints. Email is
    // attached explicitly via setUser when we choose to.
    sendDefaultPii: false,

    // Tag every event with brand identifier so multi-client deploys
    // separate cleanly in Sentry.
    initialScope: {
      tags: {
        app: BRAND.name,
      },
    },

    // Drop events from local dev unless explicitly forced. Keeps the
    // production project clean during day-to-day work.
    beforeSend(event) {
      if (typeof window === "undefined") return event;
      const isLocal =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      if (isLocal && !import.meta.env.VITE_SENTRY_FORCE_LOCAL) return null;
      return event;
    },
  });

  initialized = true;

  // Defer Replay setup until after the browser is idle, and only on
  // admin routes. The public signup form rarely needs replays — they
  // matter most when debugging admin workflows. Skipping replay on the
  // homepage saves the cost of attaching DOM mutation observers during
  // the initial render.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
    const attachReplay = () => {
      Sentry.addIntegration(
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: false,
        }),
      );
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(attachReplay, { timeout: 2000 });
    } else {
      setTimeout(attachReplay, 1000);
    }
  }
}

/** Capture an exception with optional context. */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/** Capture a non-throw event (a server-returned error, a recovered failure). */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "error",
  context?: Record<string, unknown>,
): void {
  Sentry.captureMessage(message, {
    level,
    ...(context ? { extra: context } : {}),
  });
}

/** Identify the current user for subsequent events. Pass null to clear. */
export function identifyUser(
  user: { id?: string; email?: string } | null,
): void {
  Sentry.setUser(user);
}

/** Re-export for code that needs lower-level access. */
export { Sentry };
